import { FormattedMessage, useIntl } from 'react-intl';
import StatusBar from '@rio-cloud/rio-uikit/StatusBar';

import { openPreconditionsDialog } from '@/features/app/MessageHandler';
import { ENGINE_OIL_STATE, SOURCE_DEVICE } from '@/features/engineoil/utils/constants';
import { getBarColorClass, getIconColor, getIconName, isEngineOilOutdated } from '@/features/engineoil/utils/utils';
import type { EngineOil } from '@/generated/GeneratedApiTypes';
import AnalyticsEvents from '@/utils/analytics/AnalyticsEvents';
import ActiveDtcTag from './ActiveDtcTag';
import BasePanel from './BasePanel';
import ReadingNotPossible from './ReadingNotPossible';

// Tbm3SsSeries = TG2 vehicle (TBM3 device, sensor-signal-series reading).
const Tbm3SsSeriesSCMCard = ({ engineOil }: { engineOil: EngineOil }) => {
    const intl = useIntl();
    const { state, status, measurement, measured_at, active_dtc } = engineOil;

    const isReadingNotPossible = isEngineOilOutdated(measured_at.date);
    const hasActiveDtc = active_dtc != null;
    const hasRecommendation = state !== ENGINE_OIL_STATE.OK || isReadingNotPossible;
    const recommendationKey = isReadingNotPossible ? 'reading_not_possible' : state;
    const percentage = measurement != null ? Math.round(measurement) : 0;

    const iconName = getIconName(status);
    const progressColor = getBarColorClass(status, isReadingNotPossible, hasActiveDtc);

    return (
        <BasePanel
            timestamp={measured_at.date}
            mileage={measured_at.mileage_km}
            isReadingNotPossible={isReadingNotPossible}
            testId="card-customer-tbm3-sensor_signal_series-service_care_m"
        >
            <div
                className={`display-flex ${
                    hasActiveDtc || isReadingNotPossible ? 'justify-content-between' : 'justify-content-end'
                } align-items-center margin-bottom-20`}
            >
                <div className="space-y-10">
                    {hasActiveDtc && <ActiveDtcTag dtcType={active_dtc!.type} />}

                    {isReadingNotPossible && (
                        <ReadingNotPossible
                            onHoverIntent={() => AnalyticsEvents.hoverSensorSignalSidebarOlder24h(SOURCE_DEVICE.TBM3)}
                        />
                    )}
                </div>
                <a
                    href="#"
                    className="text-color-primary"
                    onClick={(e) => {
                        e.preventDefault();
                        AnalyticsEvents.openSensorSignalMPreconditionsDialog(SOURCE_DEVICE.TBM3);
                        openPreconditionsDialog(
                            engineOil.asset_id,
                            intl.formatMessage({ id: 'intl-msg:engineOil.preconditions.dialog.title' }),
                        );
                    }}
                >
                    <FormattedMessage id="intl-msg:engineOil.seeReadingPreconditions" />
                </a>
            </div>

            <div className="display-flex margin-y-10 margin-x-25">
                <div className="width-30pct display-flex align-items-center align-self-start padding-left-2">
                    {iconName && <span className={`uikit-icon ${iconName} ${getIconColor(status)}`} />}
                    <span className="padding-left-10">
                        <FormattedMessage id="intl-msg:engineOil.engineOilLabel" />
                    </span>
                </div>
                <div className="width-70pct padding-left-15">
                    <div className="display-flex align-items-center">
                        <div className="flex-1-0 padding-right-10">
                            <StatusBar
                                progress={[
                                    { percentage, color: progressColor },
                                    { percentage: 100 - percentage, color: 'progress-bar-light' },
                                ]}
                                size="small"
                            />
                        </div>
                        <div
                            className={`width-10pct padding-right-25 text-right white-space-nowrap ${
                                isReadingNotPossible ? 'text-color-gray' : ''
                            }`}
                        >
                            <span>{percentage}%</span>
                        </div>
                    </div>
                    <div className="width-90pct margin-top-5 margin-bottom-10">
                        <div className="text-size-14 text-bold">
                            <FormattedMessage id={`intl-msg:engineOil.state.${state}`} />
                        </div>
                        {hasRecommendation && (
                            <div className="text-size-13 text-color-dark margin-top-2">
                                <FormattedMessage
                                    id={`intl-msg:engineOil.card.tbm3SsSeries.recommendation.${recommendationKey}`}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </BasePanel>
    );
};
export default Tbm3SsSeriesSCMCard;
